import { resolveNcrmActivitySourceRoute } from "./ncrmRouting.mjs";

const parsePositiveId = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const parsePayload = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

export function normalizeNcrmNotification(item = {}) {
  const id = parsePositiveId(item.id);
  if (!id) return null;
  return {
    id,
    eventType: String(item.event_type || item.eventType || "").trim().toLowerCase(),
    title: String(item.title || "").trim(),
    message: String(item.message || "").trim(),
    sourceType: String(item.source_type || item.sourceType || "").trim().toLowerCase(),
    sourceId: parsePositiveId(item.source_id ?? item.sourceId),
    targetType: String(item.target_type || item.targetType || "").trim().toLowerCase(),
    targetId: parsePositiveId(item.target_id ?? item.targetId),
    targetTab: String(item.target_tab || item.targetTab || "").trim().toLowerCase(),
    actorName: String(item.actor_name || item.actorName || "").trim(),
    payload: parsePayload(item.payload ?? item.payload_json),
    seenAt: item.seen_at || item.seenAt || null,
    archiveAfter: item.archive_after || item.archiveAfter || null,
    createdAt: item.created_at || item.createdAt || null,
  };
}

export function markNcrmNotificationItemsSeen(
  items = [],
  notificationIds = [],
  seenAt = new Date().toISOString()
) {
  const ids = new Set(
    (Array.isArray(notificationIds) ? notificationIds : [])
      .map(parsePositiveId)
      .filter(Boolean)
  );
  if (!ids.size) return Array.isArray(items) ? items : [];

  return (Array.isArray(items) ? items : []).map((item) => {
    if (!item || item.seenAt || !ids.has(parsePositiveId(item.id))) return item;
    return { ...item, seenAt };
  });
}

export function mergeNcrmNotificationItems(currentItems = [], incomingItems = []) {
  const merged = Array.isArray(currentItems) ? [...currentItems] : [];
  const knownIds = new Set(merged.map((item) => parsePositiveId(item?.id)).filter(Boolean));
  for (const item of Array.isArray(incomingItems) ? incomingItems : []) {
    const id = parsePositiveId(item?.id);
    if (!id || knownIds.has(id)) continue;
    knownIds.add(id);
    merged.push(item);
  }
  return merged;
}

export function notificationIcon(eventType) {
  const normalized = String(eventType || "").trim().toLowerCase();
  if (normalized === "activity.reassigned") return "mdi-account-switch-outline";
  if (normalized === "activity.due") return "mdi-calendar-clock-outline";
  if (normalized.startsWith("activity.")) return "mdi-calendar-check-outline";
  if (normalized.startsWith("workflow.")) return "mdi-source-branch";
  if (normalized.startsWith("import.")) return "mdi-file-import-outline";
  if (normalized.startsWith("lead.")) return "mdi-account-arrow-right-outline";
  return "mdi-bell-outline";
}

export function notificationHeadline(notification = {}, translate = (key) => key) {
  const item = normalizeNcrmNotification(notification) || notification;
  const actor = item.actorName || translate("ncrm.notifications.systemActor");
  const activity = item.payload?.activity_title || item.title;
  if (item.eventType === "activity.reassigned") {
    return translate("ncrm.notifications.events.activityReassigned", { actor, activity });
  }
  if (item.eventType === "activity.assigned") {
    return translate("ncrm.notifications.events.activityAssigned", { actor, activity });
  }
  if (item.eventType === "activity.due") {
    const key = item.payload?.activity_type === "call"
      ? "ncrm.notifications.events.activityDueCall"
      : "ncrm.notifications.events.activityDue";
    return translate(key, { activity });
  }
  return item.message || item.title;
}

export function selectNewUnseenNotifications(
  items = [],
  { afterId = 0, excludeIds = [], limit = 3 } = {}
) {
  const excluded = new Set((excludeIds || []).map(Number).filter(Number.isFinite));
  const baseline = Number(afterId) || 0;
  const maximum = Math.max(0, Number(limit) || 0);
  return (Array.isArray(items) ? items : [])
    .map(normalizeNcrmNotification)
    .filter((item) =>
      item &&
      !item.seenAt &&
      item.id > baseline &&
      !excluded.has(item.id)
    )
    .sort((left, right) => left.id - right.id)
    .slice(0, maximum);
}

export function resolveNcrmNotificationRoute(notification = {}) {
  const item = normalizeNcrmNotification(notification) || notification;
  const payload = item.payload || {};
  if (item.sourceType === "activity" || payload.activity_id) {
    const route = resolveNcrmActivitySourceRoute({
      id: payload.activity_id || item.sourceId,
      entity_type: payload.entity_type || item.targetType,
      entity_id: payload.entity_id || item.targetId,
      origin_entity_type: payload.origin_entity_type,
      origin_entity_id: payload.origin_entity_id,
      origin_board_id: payload.origin_board_id,
      onboarding_id: payload.onboarding_id,
    });
    if (route) return route;
  }

  if (item.targetType === "deal" && item.targetId) {
    return {
      name: "NcrmDealDetails",
      params: { id: String(item.targetId) },
      query: { tab: item.targetTab || "activity" },
    };
  }
  if (item.targetType === "contract" && item.targetId) {
    return {
      name: "NcrmContractDetails",
      params: { contractId: String(item.targetId) },
      query: { tab: item.targetTab || "activity" },
    };
  }
  if (item.targetType === "company" && item.targetId) {
    return {
      name: "NcrmCompanyDetails",
      params: { id: String(item.targetId) },
      query: { tab: item.targetTab || "activity" },
    };
  }
  if (item.targetType === "person" && item.targetId) {
    return {
      name: "NcrmPersonDetails",
      params: { id: String(item.targetId) },
      query: { tab: item.targetTab || "activity" },
    };
  }
  return { path: "/ncrm/activities" };
}

export function notificationAgeMeta(value, now = new Date()) {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return { kind: "unknown", value: 0 };
  const minutes = Math.max(0, Math.floor((now.getTime() - timestamp) / 60000));
  if (minutes < 1) return { kind: "now", value: 0 };
  if (minutes < 60) return { kind: "minutes", value: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { kind: "hours", value: hours };
  return { kind: "date", value: timestamp };
}
